import { Directive, ElementRef, Input, OnChanges, OnDestroy, Renderer2 } from '@angular/core';
import { NgControl } from '@angular/forms';

// Stored in the control to mark a field for clearing; removeEmptyFields turns it into a real null.
export const NULL_VALUE = 'null';

// Adds a "use default" checkbox after an input so a bulk edit can clear the field on every
// selected object.  The input keeps its own type, classes and attributes.
@Directive({
  selector: 'input[nullable]'
})
export class NullableDirective implements OnChanges, OnDestroy {
  @Input() nullable: boolean = false;

  private label: any;
  private previousValue: any = null;
  private unlisten: () => void;

  constructor(private ngControl: NgControl, private elementRef: ElementRef, private renderer: Renderer2) {}

  ngOnChanges() {
    if (this.nullable && !this.label) {
      this.addCheckbox();
    }
    else if (!this.nullable && this.label) {
      this.removeCheckbox();
    }
  }

  ngOnDestroy() {
    this.removeCheckbox();
  }

  private get isNull(): boolean {
    const control = this.ngControl.control;

    return !!control && (control.value === NULL_VALUE);
  }

  private addCheckbox() {
    const input = this.elementRef.nativeElement;
    const id = input.id ? (input.id + '_is_null') : null;
    const checkbox = this.renderer.createElement('input');

    this.renderer.setAttribute(checkbox, 'type', 'checkbox');
    this.renderer.setProperty(checkbox, 'checked', this.isNull);

    this.label = this.renderer.createElement('label');
    this.renderer.addClass(this.label, 'mt-2');

    if (id) {
      this.renderer.setAttribute(checkbox, 'id', id);
      this.renderer.setAttribute(this.label, 'for', id);
    }

    this.renderer.appendChild(this.label, checkbox);
    this.renderer.appendChild(this.label, this.renderer.createText(' Use default'));
    this.renderer.insertBefore(input.parentNode, this.label, input.nextSibling);

    this.unlisten = this.renderer.listen(checkbox, 'change', (event) => {
      this.toggle(event.target.checked);
    });

    this.setReadonly(this.isNull);
  }

  private removeCheckbox() {
    if (this.unlisten) {
      this.unlisten();
      this.unlisten = null;
    }

    if (this.label) {
      this.renderer.removeChild(this.label.parentNode, this.label);
      this.label = null;
    }

    this.setReadonly(false);
  }

  private toggle(checked: boolean) {
    const control = this.ngControl.control;

    if (control) {
      if (checked) {
        this.previousValue = control.value;
        control.setValue(NULL_VALUE);
      }
      else {
        control.setValue(this.previousValue);
      }

      control.markAsDirty();
    }

    this.setReadonly(checked);
  }

  private setReadonly(readonly: boolean) {
    this.renderer.setProperty(this.elementRef.nativeElement, 'readOnly', readonly);
  }
}
